<template>
  <div class="work-bench-info-container">
    <el-scrollbar>
      <div class="work-bench-info-item" v-for="(item, index) of filteredDataList" :key="index" @click="dataListClick(item)">
        <div class="control">
          <svg-icon v-if="relationTypeSvg[item[typeProp]]"
            :iconName="item[loadingProp] ? 'loading' : relationTypeSvg[item[typeProp]]"></svg-icon>
        </div>
        <div class="title ellipsis" :title="item[dataTextProp]">{{ item[dataTextProp] }}</div>
        <div class="visibility" @click.stop="toggleVisibility(item)" :title="item.hidden ? '显示' : '隐藏'">
          <svg-icon :iconName="item.hidden ? 'hide' : 'show'"></svg-icon>
        </div>
        <div class="remove" @click.stop="dataRemove(item)" title="删除">
          <svg-icon iconName="minus-box"></svg-icon>
        </div>
      </div>
    </el-scrollbar>

    <teleport to="#home-body">
      <EditPopup class="manage-panel-exposure-record-Charts" v-if="chartsList.length > 0" title="线索区数据"
        :controls="chartsControls" :titleDrag="true">
        <div class="charts-wrapper">
          <FrequencyChart :showFlag="true" :dataList="chartsList" ref="frequencyChartRef"></FrequencyChart>
        </div>
      </EditPopup>
      <WorkBenchFeatureInfo :mapId="props.mapItem.mapId" />
    </teleport>

    <!-- 独立组件化模块 -->
    <WorkBenchAnnotation :mapId="props.mapItem.mapId" />
    <WorkBenchAnalysisLayer :mapId="props.mapItem.mapId" />
  </div>
</template>

<script setup>
import {
  onBeforeMount, onMounted, onUnmounted, onUpdated,
  computed, watch, ref, nextTick, reactive, markRaw,
  h, defineComponent, toRaw,
} from 'vue';
import store from '@/store';
import { ElMessageBox } from 'element-plus';
import { isNull, dayjs, message, vueCreateAppInit } from '@/utils/js/initVariable';

import FrequencyChart from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/CustomLocaleControl/components/Frequency/components/FrequencyChart';
import EditPopup from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/CustomLocaleControl/components/EditPopup';
import { useMapDrawControl } from '@/composables/useMapDrawControl';
import { fileUploadControlInit } from '@/composables/useFileUploadControl';

import WorkBenchAnnotation from '../components/WorkBenchAnnotation';
import WorkBenchAnalysisLayer from '../components/WorkBenchAnalysisLayer';
import WorkBenchFeatureInfo from '../components/WorkBenchFeatureInfo';

import { getRelatedData, removeBaseMapData } from '@/api/BaseMapApi';
import {
  shootLayerUse, imageLayerUse, geojsonUse, exposureLayerUse,
  regionGeoUse,
} from '@/views/WorkBench/utils';

import SvgIcon from '@/components/SvgIcon';


import xlsx from 'xlsx-js-style';

// props
const props = defineProps({
  mapItem: { type: Object, },
  search: { type: String, default: '' }
});
const emit = defineEmits(['dataLoaded']);

const homeMap = computed(() => store.getters['getHomeMap']),
  map = toRaw(homeMap.value),
  layerGroup = L.featureGroup().addTo(map),
  layerGroupClose = () => {
    map.removeLayer(layerGroup);
  },
  layerGroupClear = () => {
    layerGroup.clearLayers();
  }

// 初始化绘制控制 composable
const { customLocaleControlInit, bindDrawStop, unbindDrawStop } = useMapDrawControl(map, layerGroup, {
  onFileUpload: (event) => {
    const marker = event.layer;
    fileUploadControlInit(marker, {});
    layerGroup.addLayer(marker);
  },
});

const defaultTileLayer = L.tileLayer(props.mapItem['mapUrl'], {
  zIndex: 10,
}).addTo(map),
defaultTileLayerClear = () => {
  if (map.hasLayer(defaultTileLayer)) {
    map.removeLayer(defaultTileLayer);
  }
}

const relationTypeSvg = reactive({
  'area_image': 'image',
  'shooting': 'film',
  'location_data': 'polygon-icon',
  'region_directory': 'polygon-icon',
  'exposure': 'chart',
})

const chartsList = ref([]), chartsItem = ref('');
const frequencyChartRef = ref();
const chartDownloadFn = () => {
  frequencyChartRef.value && frequencyChartRef.value['chartDownloadFn'] && frequencyChartRef.value['chartDownloadFn'](chartsItem.value.name);
}

const downloadList = [
  { name: '日期', prop: 'exposureTime' },
  { name: '次数', prop: 'count' },
],
  excelHeader = downloadList.map(e => {
    return e.name;
  })
const downloadExposureFn = () => {
  if (store.getters['isSuperAdmin']) {
    exportToExcel(chartsItem.value.dataList);
    return
  }
  if (!chartsList.value) {
    message('请选择线索数据', 2);
    return;
  }
  const data = [
    excelHeader,
    ...(
      chartsList.value.map(item => {
        return downloadList.map(e => {
          return item[e.prop];
        })
      })
    )
  ];
  const ws = xlsx.utils.aoa_to_sheet(data);
  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
  xlsx.writeFile(wb, `${chartsItem.value.name}.xlsx`);
}
const exportToExcel = (rawData) => {
  const map = {}
  rawData.forEach(item => {
    const type = item.scanSatelliteType
    if (!map[type]) map[type] = { type, count: 0, times: [] }
    map[type].count++
    map[type].times.push(item.scanProductTime)
  })
  const centerStyle = { alignment: { horizontal: 'center', vertical: 'center' } }
  const boldCenter = { font: { bold: true }, alignment: { horizontal: 'center' } }
  const header = [
    { v: '卫星类型', s: boldCenter },
    { v: '数量', s: boldCenter },
    { v: '拍摄时间', s: boldCenter }
  ]
  const items = Object.values(map)
  const totalCount = items.reduce((sum, item) => sum + item.count, 0)
  const rows = items.map(item => [
    { v: item.type, s: centerStyle },
    { v: item.count, t: 'n', s: centerStyle },
    { v: item.times.join('\n'), s: { alignment: { wrapText: true, vertical: 'center' } } }
  ])
  const totalRow = [
    { v: '总计', s: { font: { bold: true }, alignment: { horizontal: 'center', vertical: 'center' } } },
    { v: totalCount, t: 'n', s: { font: { bold: true }, alignment: { horizontal: 'center', vertical: 'center' } } },
    { v: '', s: {} }
  ]
  const ws = xlsx.utils.aoa_to_sheet([header, ...rows, totalRow])
  ws['!cols'] = [{ wch: 25 }, { wch: 8 }, { wch: 25 }]
  rows.forEach((row, i) => {
    const lineCount = row[2].v.split('\n').length
    if (!ws['!rows']) ws['!rows'] = []
    ws['!rows'][i + 1] = { hpt: Math.max(20, lineCount * 16) }
  })
  const wb = xlsx.utils.book_new()
  xlsx.utils.book_append_sheet(wb, ws, `${chartsItem.value.name}`)
  xlsx.writeFile(wb, `${chartsItem.value.name}.xlsx`)
}

const chartsControls = [
  { iconName: 'download-center', fill: 'rgb(102, 203, 118)', title: '数据导出', onClick: downloadExposureFn, },
  { iconName: 'image', fill: 'rgb(102, 203, 118)', title: '图片导出', onClick: chartDownloadFn, },
  { iconName: 'close-box', fill: 'rgb(6, 180, 221)', onClick: () => { chartsList.value = [] }, },
]

const dataTextProp = ref('data-text'), typeProp = ref('relationType');
const typeChangeText = (target) => {
  const type = target[typeProp.value];
  switch (type) {
    case 'location_data': return `${target.name} ${target.createTime || ''}`;
    case 'region_directory': return `${target.regionName} ${target.createTime || ''}`;
    case 'area_image': return `${target.satelliteName} ${target.satelliteSource} ${target.shootingTime}`;
    case 'shooting': return `${target.updateTime} ${target.videoFileName}`;
    case 'exposure': return `${target.name} ${target.startTime} ~ ${target.endTime}`;
    default: return '';
  }
}

const loadingProp = ref('loading'), layerProp = 'layer';

const toggleVisibility = (item) => {
  const layer = item[layerProp];
  if (!layer) return;
  if (item.hidden) {
    layerGroup.addLayer(layer);
    item.hidden = false;
  } else {
    item._toggleHiding = true;
    layerGroup.removeLayer(layer);
    item._toggleHiding = false;
    item.hidden = true;
  }
};

const dataListClick = (item) => {
  const layer = item[layerProp], type = item[typeProp.value];
  if (type == 'exposure') {
    chartsList.value = item['relationObject']['dateExposureList'] || [];
    chartsItem.value = item;
  }
  if (!layer) return;
  if (layer.getBounds) {
    const bounds = layer.getBounds();
    bounds.isValid() && map.fitBounds(bounds, {
      maxZoom: 13,
    });
  } else if (layer.getLatLng) {
    const latlng = layer.getLatLng();
    map.setView(latlng, 13);
  }
}

const imageInitFn = async (target, item) => {
  if (!target['id']) return;
  item[loadingProp.value] = true;
  const tifLayer = await imageLayerUse(target);
  item[loadingProp.value] = false;
  if (tifLayer) {
    item[layerProp] = tifLayer.addTo(layerGroup);
  }
}
import { getFileUploadIcon } from '@/components/FileUploadPopup/useFileUploadIcon';

const shootInitFn = async (target, item) => {
  const icon = getFileUploadIcon();
  const marker = L.marker(target, { icon });
  
  // 绑定移除事件释放 Vue 组件内存（仅在真正删除时，不在显隐切换时）
  marker.on('remove', () => {
    if (!item._toggleHiding && icon._vueApp) icon._vueApp.unmount();
  });

  item[layerProp] = marker.addTo(layerGroup);
  fileUploadControlInit(marker, target);
}
const locationInitFn = async (target, item) => {
  const json = JSON.parse(target['geom']),
    geojson = await geojsonUse(json);
  item[layerProp] = geojson.addTo(layerGroup);
  customLocaleControlInit(geojson, target);
}
const regionInitFn = async (target, item) => {
  const geojson = await regionGeoUse(target);
  item[layerProp] = geojson.addTo(layerGroup);
  customLocaleControlInit(geojson, target);
}
const exposureInitFn = async (target, item) => {
  const data = target['dataList'],
    group = await exposureLayerUse(data);
  item[layerProp] = group.addTo(layerGroup);
}
const dataInitFn = {
  'area_image': imageInitFn,
  'shooting': shootInitFn,
  'location_data': locationInitFn,
  'region_directory': regionInitFn,
  'exposure': exposureInitFn,
}
const workBenchDataInit = (data) => {
  data.forEach(item => {
    const dataType = item[typeProp.value],
      target = item['relationObject'];
    dataInitFn[dataType] && dataInitFn[dataType](target, item);
  });
}

const dataList = ref([]);
const filteredDataList = computed(() => {
  if (!props.search) return dataList.value;
  return dataList.value.filter(item => {
    const text = item[dataTextProp.value] || '';
    return text.includes(props.search);
  });
})

const currentWorkbenchId = ref('');

// relationType → 后端 targetType 映射（用于删除）
const relationTypeToTargetType = {
  'shooting': 't_shooting_location',
  'area_image': 't_area_image',
  'exposure': 't_exposure',
  'region_directory': 'region_directory',
};

// 删除关联数据
const dataRemove = (item) => {
  const targetType = relationTypeToTargetType[item[typeProp.value]];
  const targetId = item['relationObject']?.['id'] || item['id'];
  if (!targetType || !targetId) return;
  ElMessageBox.confirm('确认移除该数据？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(() => {
    removeBaseMapData({
      mapId: props.mapItem['mapId'],
      targetType,
      targetId,
    }).then(res => {
      if (res.data.code == 1004) {
        // 移除图层
        const layer = item[layerProp];
        layer && layerGroup.removeLayer(layer);
        // 从列表移除
        dataList.value = dataList.value.filter(e => e !== item);
        message('移除成功', 1);
      }
    }).catch(err => {
      console.error('删除数据失败:', err);
    });
  }).catch(() => {});
}

const queryWorkbenchContentFn = async () => {
  layerGroupClear();
  try {
    const res = await getRelatedData(props.mapItem['mapId']);
    if (res.data.code == 1004) {
      const raw = res.data.data || {};
      // 将后端分类返回的数据转换为统一的 dataList 格式
      const unified = [
        ...(raw.regionDirectoryList || []).map(e => ({ relationType: 'region_directory', relationObject: e, ...e })),
        ...(raw.exposureList || []).map(e => ({ relationType: 'exposure', relationObject: e, ...e })),
        ...(raw.imageList || []).map(e => ({ relationType: 'area_image', relationObject: e, ...e })),
        ...(raw.videoList || []).map(e => ({ relationType: 'shooting', relationObject: e, ...e })),
      ];
      dataList.value = unified.map(item => {
        item[dataTextProp.value] = typeChangeText(item);
        return item;
      });
      workBenchDataInit(dataList.value);
    } else {
      dataList.value = [];
    }
  } catch (err) {
    console.error('查询底图数据失败:', err);
  }
  emit('dataLoaded');
}

// 暴露给外部调用
defineExpose({ refreshDataList: queryWorkbenchContentFn });

// 组件挂载后
onMounted(() => {
  queryWorkbenchContentFn();
  bindDrawStop();
});
// 组件销毁后
onUnmounted(() => {
  unbindDrawStop();
  layerGroupClose();
  defaultTileLayerClear();
});

</script>

<style lang='less'>
.manage-panel-exposure-record-Charts{
  left: 10px;
  bottom: 10px;
  position: fixed;
  height: 420px !important;
  width: 800px !important;
  display: flex;
  flex-direction: column;
  .custom-locale-control-edit-popup-body{
    flex: 1;
    min-height: 0;
    .charts-wrapper{
      height: 100%;
    }
  }
}
</style>

<style lang='less' scoped>
.work-bench-info-container {
  display: flex;
  flex-direction: column;

  :deep(.el-scrollbar) {
    flex: 1;
    min-height: 0;

    .el-scrollbar__view {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }

  .work-bench-info-item {
    cursor: pointer;
    min-height: 28px;
    background-color: var(--backgroundColor_38);
    border-radius: var(--borderRadiusSize);
    padding: 4px 8px;
    display: flex;
    gap: 6px;
    align-items: center;
    transition: background-color 0.2s;
    border-left: 2px solid transparent;

    &:hover {
      background-color: var(--backgroundColor_16);
    }

    .title {
      flex: 1;
      min-width: 0;
      font-size: 13px;
      line-height: 1.3;
      color: var(--fontColor_5);
    }

    .control {
      width: max-content;
      display: flex;
      gap: 4px;
      flex-shrink: 0;
    }

    .remove {
      width: max-content;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s;
      display: flex;
      align-items: center;
      justify-content: center;
      .svg-icon {
        width: 14px;
        height: 14px;
        &:hover {
          fill: var(--el-color-danger);
        }
      }
    }

    &:hover .remove {
      opacity: 1;
    }

    .visibility {
      cursor: pointer;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      .svg-icon {
        width: 14px;
        height: 14px;
      }
    }
  }

}</style>
